import React from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Dimensions,
  Animated,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Image,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { spacing, typography } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { LinearGradient } from "expo-linear-gradient";

const { width } = Dimensions.get("window");

const FEATURES = [
  {
    id: "1",
    icon: "shopping-bag",
    title: "Easy Shopping",
    description: "Browse and buy from local vendors",
  },
  {
    id: "2",
    icon: "users",
    title: "Multi-vendor",
    description: "Shop from multiple sellers",
  },
  {
    id: "3",
    icon: "map-pin",
    title: "Local Delivery",
    description: "Fast delivery service",
  },
  {
    id: "4",
    icon: "shield",
    title: "Secure Payments",
    description: "Safe and reliable transactions",
  },
  {
    id: "5",
    icon: "message-circle",
    title: "Chat with Sellers",
    description: "Direct communication with vendors",
  },
  {
    id: "6",
    icon: "heart",
    title: "Support Local",
    description: "Help local businesses thrive",
  },
];

export function AboutSection() {
  const colors = useColors();
  const styles = createStyles(colors);
  const scrollY = React.useRef(new Animated.Value(0)).current;

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [1, 0.9],
    extrapolate: "clamp",
  });

  const headerTranslateY = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [0, -20],
    extrapolate: "clamp",
  });

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    scrollY.setValue(offsetY);
  };

  return (
    <ScrollView
      style={styles.container}
      showsVerticalScrollIndicator={false}
      onScroll={handleScroll}
      scrollEventThrottle={16}
    >
      <Animated.View
        style={[
          styles.header,
          {
            opacity: headerOpacity,
            transform: [{ translateY: headerTranslateY }],
          },
        ]}
      >
        <LinearGradient
          colors={[`${colors.primary}20`, `${colors.primary}10`]}
          style={styles.headerGradient}
        >
          <View style={styles.logoContainer}>
            <Image
              source={require("../../assets/icon.png")}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>
          <Text style={styles.sectionTitle}>JadeTrade</Text>
          <Text style={styles.description}>
            Fashion Delivered Fast Like Takeout.
          </Text>
        </LinearGradient>
      </Animated.View>

      <View style={styles.missionSection}>
        <Text style={styles.missionTitle}>Our Mission</Text>
        <Text style={styles.missionText}>
          JadeTrade connects local sellers with customers looking for unique
          products. We're committed to supporting small businesses and creating
          a sustainable marketplace that benefits our community.
        </Text>
      </View>

      <Text style={styles.featuresTitle}>What We Offer</Text>
      <View style={styles.features}>
        {FEATURES.map((feature) => (
          <View
            key={feature.id}
            style={[styles.featureCard, { marginBottom: spacing.md }]}
          >
            <LinearGradient
              colors={[colors.backgroundSecondary, colors.background]}
              style={styles.featureGradient}
            >
              <View style={styles.iconContainer}>
                <Feather
                  name={feature.icon as any}
                  size={24}
                  color={colors.primary}
                />
              </View>
              <View style={styles.featureContent}>
                <Text style={styles.featureTitle}>{feature.title}</Text>
                <Text style={styles.featureDescription}>
                  {feature.description}
                </Text>
              </View>
            </LinearGradient>
          </View>
        ))}
      </View>
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: spacing.xl,
  },
  headerGradient: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: spacing.xl * 1.5,
    paddingHorizontal: spacing.lg,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  logoContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: spacing.md,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    overflow: "hidden",
  },
  logo: {
    width: "100%",
    height: "100%",
  },
  sectionTitle: {
    ...typography.title,
    fontSize: 32,
    color: colors.primary,
    textAlign: "center",
    marginBottom: spacing.xs,
    fontWeight: "700",
  },
  description: {
    ...typography.body,
    fontSize: 18,
    color: colors.textLight,
    textAlign: "center",
    marginTop: spacing.xs,
  },
  missionSection: {
    paddingHorizontal: spacing.md,
    marginBottom: spacing.xl,
  },
  missionTitle: {
    ...typography.title,
    fontSize: 22,
    color: colors.text,
    marginBottom: spacing.md,
    fontWeight: "600",
  },
  missionText: {
    ...typography.body,
    fontSize: 16,
    color: colors.textLight,
    lineHeight: 24,
  },
  featuresTitle: {
    ...typography.title,
    fontSize: 22,
    color: colors.text,
    marginBottom: spacing.md,
    paddingHorizontal: spacing.md,
    fontWeight: "600",
  },
  features: {
    paddingHorizontal: spacing.md,
    marginBottom: spacing.xl,
  },
  featureCard: {
    width: "100%",
    borderRadius: 16,
    overflow: "hidden",
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  featureGradient: {
    flexDirection: "row",
    alignItems: "center",
    padding: spacing.md,
    borderRadius: 16,
  },
  iconContainer: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
    marginRight: spacing.md,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  featureContent: {
    flex: 1,
  },
  featureTitle: {
    ...typography.subtitle,
    fontSize: 18,
    color: colors.text,
    marginBottom: spacing.xs,
    fontWeight: "600",
  },
  featureDescription: {
    ...typography.body,
    fontSize: 14,
    color: colors.textLight,
  },
});
